import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Section, StatusPill, EmptyState } from "@/components/app/Primitives";
import { ReviewConfirmDialog } from "@/components/app/ReviewConfirmDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import TID from "@/lib/testIds";

export default function Payments() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pct, setPct] = useState(50);
  const [orderId, setOrderId] = useState("");
  const isInfluencer = user.role === "influencer";
  const isCustomer = user.role === "customer";

  const load = useCallback(() => api.get("/payments").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
    if (isInfluencer) {
      api.get("/orders").then((r) =>
        setOrders(r.data.filter((o) => ["po_issued", "in_progress", "delivered"].includes(o.status)))
      );
    }
  }, [load, isInfluencer]);

  const requestAdvance = async () => {
    if (!orderId) return toast.error("Select an order");
    try { await api.post("/payments/advance-request", { order_id: orderId, percentage: pct, reason: "Kick-off" }); toast.success("Advance requested"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const requestFinal = async () => {
    if (!orderId) return toast.error("Select an order");
    try { await api.post("/payments/final-request", { order_id: orderId }); toast.success("Final payment requested"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const approve = async (p, comment) => { await api.post(`/payments/${p.id}/approve`, { decision: "approved", comment }); toast.success("Approved"); load(); };
  const reject = async (p, comment) => { await api.post(`/payments/${p.id}/approve`, { decision: "rejected", comment }); toast.success("Rejected"); load(); };
  const disburse = async (p) => { await api.post(`/payments/${p.id}/disburse`); toast.success("Payment released"); load(); };

  const selectedOrder = orders.find((o) => o.id === orderId);

  return (
    <div className="space-y-6">
      <Section
        title="Payments"
        subtitle={isInfluencer ? "Request advances against your PO or a final payment on completion." : isCustomer ? "Approve advance and final payments requested by influencers." : "Release approved payments to influencers."}
      />

      {isInfluencer && orders.length > 0 && (
        <div className="bg-white rounded-xl border p-5 grid grid-cols-1 md:grid-cols-6 gap-3 md:items-end">
          <div className="md:col-span-3 min-w-0">
            <label className="text-xs font-semibold">Order</label>
            <Select value={orderId} onValueChange={setOrderId}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Select an active order" /></SelectTrigger>
              <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.campaign} — {o.order_number}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1 min-w-0">
            <label className="text-xs font-semibold">Advance %</label>
            <Select value={String(pct)} onValueChange={(v) => setPct(parseInt(v))}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25%</SelectItem>
                <SelectItem value="50">50%</SelectItem>
                <SelectItem value="75">75%</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex flex-col sm:flex-row gap-2 min-w-0">
            <ReviewConfirmDialog
              trigger={<Button className="bg-[#0052FF] hover:bg-[#0043D9] w-full sm:w-auto flex-1">Request advance</Button>}
              title="Request advance payment"
              subtitle="ProPay will pass this request to the FMCG customer for approval and then release funds."
              details={{
                Campaign: selectedOrder?.campaign || "—",
                Order: selectedOrder?.order_number || "—",
                "Advance %": `${pct}%`,
                "Approx amount": selectedOrder ? `${selectedOrder.currency} ${Math.round(selectedOrder.order_value * pct / 100).toLocaleString()}` : "—",
              }}
              confirmLabel="Send request"
              confirmTone="primary"
              onConfirm={requestAdvance}
              testIdOpen={TID.advanceRequestBtn}
              testIdConfirm="confirm-advance-request"
            />
            <ReviewConfirmDialog
              trigger={<Button variant="outline" className="w-full sm:w-auto flex-1">Request final</Button>}
              title="Request final payment"
              subtitle="Available only after every PO line is approved by the FMCG customer."
              details={{
                Campaign: selectedOrder?.campaign || "—",
                Order: selectedOrder?.order_number || "—",
                "Payment type": "Final (balance after advances)",
              }}
              confirmLabel="Send request"
              confirmTone="primary"
              onConfirm={requestFinal}
              testIdOpen={TID.finalRequestBtn}
              testIdConfirm="confirm-final-request"
            />
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="No payments yet" hint="Payment requests and disbursements will appear here." />
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
              <tr>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Influencer</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Early Payment Fee</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-3 capitalize font-semibold">{p.type}</td>
                  <td className="px-4 py-3">{p.influencer_name}</td>
                  <td className="px-4 py-3 text-right font-mono">{p.currency} {Number(p.amount).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-xs text-slate-500">{p.early_payment_fee ? `${p.currency} ${Number(p.early_payment_fee).toLocaleString()}` : "—"}</td>
                  <td className="px-4 py-3"><StatusPill status={p.status} /></td>
                  <td className="px-4 py-3 text-right">
                    {(isCustomer || user.role === "propay_admin") && p.status === "requested" && (
                      <>
                        <ReviewConfirmDialog
                          trigger={<Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">Approve</Button>}
                          title={`Approve ${p.type} payment?`}
                          subtitle="You're authorising ProPay to release this amount to the influencer."
                          details={{
                            Influencer: p.influencer_name,
                            "Payment type": p.type,
                            Amount: `${p.currency} ${Number(p.amount).toLocaleString()}`,
                            "Early Payment Fee": p.early_payment_fee ? `${p.currency} ${Number(p.early_payment_fee).toLocaleString()} (${p.days_fronted} days × ${p.early_payment_rate_per_30}%/30d)` : "—",
                            Reason: p.reason || "—",
                          }}
                          confirmLabel="Approve payment"
                          confirmTone="success"
                          onConfirm={(c) => approve(p, c)}
                          testIdOpen={TID.paymentApprove(p.id)}
                          testIdConfirm={`confirm-payment-approve-${p.id}`}
                        />
                        <span className="ml-2 inline-block">
                          <ReviewConfirmDialog
                            trigger={<Button size="sm" variant="destructive">Reject</Button>}
                            title={`Reject ${p.type} payment?`}
                            subtitle="The influencer will be notified with your reason."
                            details={{
                              Influencer: p.influencer_name,
                              Amount: `${p.currency} ${Number(p.amount).toLocaleString()}`,
                            }}
                            confirmLabel="Reject"
                            confirmTone="danger"
                            requireComment commentRequired commentLabel="Reason for rejection"
                            onConfirm={(c) => reject(p, c)}
                            testIdConfirm={`confirm-payment-reject-${p.id}`}
                          />
                        </span>
                      </>
                    )}
                    {["procurement_desk", "propay_admin"].includes(user.role) && p.status === "customer_approved" && (
                      <ReviewConfirmDialog
                        trigger={<Button size="sm" className="bg-[#0052FF] hover:bg-[#0043D9]">Release payment</Button>}
                        title="Release payment to influencer?"
                        subtitle="Manual bank release — ProPay Engage360 captures the status. Confirm the bank transfer has been initiated on your side."
                        details={{
                          Influencer: p.influencer_name,
                          "Payment type": p.type,
                          Amount: `${p.currency} ${Number(p.amount).toLocaleString()}`,
                          Approvals: (p.approvals || []).map((a) => `${a.role} ${a.decision}`).join(" · ") || "—",
                          "Early Payment Fee (billed to FMCG)": p.early_payment_fee ? `${p.currency} ${Number(p.early_payment_fee).toLocaleString()}` : "—",
                        }}
                        confirmLabel="Confirm release"
                        confirmTone="primary"
                        onConfirm={() => disburse(p)}
                        testIdOpen={TID.paymentDisburse(p.id)}
                        testIdConfirm={`confirm-payment-disburse-${p.id}`}
                      />
                    )}
                    {p.status === "paid" && p.payment_ref && <span className="text-xs font-mono text-slate-500">{p.payment_ref}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
